import React from "react";
import { connect } from "react-redux";
import {
  Container, Row, Col, Card, CardHeader, CardBody, Button, Badge as BadgeUI,
  Input, Modal, ModalHeader, ModalBody, ModalFooter
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import Swal from "sweetalert2";
import api from "./../../../Api.js";
import SimpleHeader from "./../../../components/Headers/SimpleHeader.jsx";
import HpBar from "./HpBar.jsx";
import GoldDisplay from "./GoldDisplay.jsx";
import QuestionBankModal from "./QuestionBankModal.jsx";

/**
 * TeacherDungeonMaster — painel do professor (Mestre do Jogo).
 * Permite: iniciar sessao, dar/tirar XP/Gold/HP, spawnar inimigos/eventos.
 */
class TeacherDungeonMaster extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      classroomId: parseInt(this.getClassroomFromUrl()),
      session: null,
      roster: [],
      loading: true,
      modalAction: null,  // {userId, type}
      actionAmount: 10,
      actionReason: '',
      questionBankOpen: false,
      questionCount: null
    };
    this.pollTimer = null;
  }

  getClassroomFromUrl() {
    var match = window.location.pathname.match(/dm\/(\d+)/);
    return match ? match[1] : null;
  }

  async componentDidMount() {
    await this.loadRoster();
    await this.restoreSession();
    await this.loadQuestionCount();
    this.pollTimer = setInterval(() => this.loadRoster(), 8000);
  }

  // Restaura a sessão ativa depois de um F5 (antes ficava só no estado local)
  async restoreSession() {
    if (!this.state.classroomId) return;
    try {
      var res = await api.getActiveSession(this.state.classroomId);
      if (res.data && res.data.active && res.data.session) {
        var s = res.data.session;
        this.setState({ session: { ...s, bossEligible: s.classNumber % 4 === 0 } });
      }
    } catch (e) {
      this.notify('danger', 'Erro', 'Falha ao verificar sessão ativa da turma');
    }
  }

  async loadQuestionCount() {
    if (!this.state.classroomId) return;
    try {
      var res = await api.dmListQuestions(this.state.classroomId);
      this.setState({ questionCount: (res.data || []).length });
    } catch (e) {
      this.notify('danger', 'Erro', 'Falha ao carregar o Banco de Perguntas');
    }
  }

  componentWillUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  }

  async loadRoster() {
    if (!this.state.classroomId) return;
    try {
      var res = await api.dmRoster(this.state.classroomId);
      this.setState({ roster: res.data || [], loading: false });
    } catch (e) {
      this.setState({ loading: false });
    }
  }

  notify(type, title, message) {
    try {
      this.refs.notificationAlert.notificationAlert({
        place: 'tr',
        message: (
          <div className="alert-text">
            <span className="alert-title">{title}</span>
            <span> {message}</span>
          </div>
        ),
        type: type,
        icon: 'ni ni-bell-55',
        autoDismiss: 4
      });
    } catch (e) {}
  }

  async startSession() {
    try {
      var res = await api.dmStartSession(this.state.classroomId);
      // 201 -> sessão nova direto no data; 200 -> {session, message: 'Sessao ja ativa'}
      var s = res.data.session || res.data;
      var session = {
        ...s,
        bossEligible: s.bossEligible !== undefined ? s.bossEligible : s.classNumber % 4 === 0
      };
      this.setState({ session: session });
      this.notify('success', 'Sessão iniciada!', 'Aula ' + session.classNumber + (session.bossEligible ? ' — BOSS hoje!' : ''));
    } catch (e) {
      this.notify('danger', 'Erro', 'Falha ao iniciar sessão');
    }
  }

  async endSession() {
    if (!this.state.session) return;
    var confirm = await Swal.fire({
      title: 'Encerrar a sessão?',
      html: 'A aventura da <strong>Aula ' + this.state.session.classNumber + '</strong> termina para todos os alunos.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: '<i class="fas fa-stop"></i> Encerrar',
      cancelButtonText: '<i class="fas fa-times"></i> Continuar jogando'
    });
    if (!confirm.isConfirmed) return;
    try {
      await api.dmEndSession(this.state.session.id);
      this.setState({ session: null });
      this.notify('info', 'Sessão encerrada', '');
    } catch (e) {
      this.notify('danger', 'Erro', 'Falha ao encerrar sessão');
    }
  }

  async spawnEnemy(type) {
    if (!this.state.session) {
      this.notify('warning', 'Sessão necessária', 'Inicie a sessão primeiro');
      return;
    }
    try {
      var res = await api.dmSpawnEnemy(this.state.session.id, { type: type });
      this.notify('success', '⚔️ ' + (res.data.name || 'Inimigo'), 'apareceu na turma!');
    } catch (e) {
      var msg = (e.response && e.response.data && e.response.data.error) || 'Falha ao spawnar inimigo';
      this.notify('danger', 'Erro', msg);
    }
  }

  async spawnEvent() {
    if (!this.state.session) {
      this.notify('warning', 'Sessão necessária', 'Inicie a sessão primeiro');
      return;
    }
    try {
      var res = await api.dmSpawnEvent(this.state.session.id);
      this.notify('success', '🎲 ' + (res.data.title || 'Evento Aleatório'), res.data.description || 'Algo aconteceu na turma!');
    } catch (e) {
      var msg = (e.response && e.response.data && e.response.data.error) || 'Falha ao lançar evento';
      this.notify('danger', 'Erro', msg);
    }
  }

  openAction(userId, type) {
    this.setState({
      modalAction: { userId, type },
      actionAmount: type === 'damage' ? 10 : 20,
      actionReason: ''
    });
  }

  closeAction() {
    this.setState({ modalAction: null });
  }

  // Limite por ação: 50 pra HP (dano/cura), 500 pra XP, 200 pra Gold
  getActionLimit(type) {
    if (type === 'damage' || type === 'heal') return 50;
    if (type === 'award-xp') return 500;
    return 200;
  }

  getActionUnit(type) {
    if (type === 'damage' || type === 'heal') return 'HP';
    if (type === 'award-xp') return 'XP';
    return 'Gold';
  }

  async confirmAction() {
    var ma = this.state.modalAction;
    if (!ma) return;

    var amount = parseInt(this.state.actionAmount) || 0;
    var limit = this.getActionLimit(ma.type);

    if (amount < 1) {
      this.notify('warning', 'Quantidade inválida', 'Informe um valor maior que zero');
      return;
    }
    if (amount > limit) {
      this.notify('warning', '🛑 Limite excedido!', 'O máximo é ' + limit + ' ' + this.getActionUnit(ma.type) + ' por ação');
      return;
    }

    var data = {
      amount: amount,
      classroom: this.state.classroomId,
      reason: this.state.actionReason || ma.type
    };

    try {
      var fn = null;
      if (ma.type === 'award-xp') fn = api.dmAwardXp;
      if (ma.type === 'award-gold') fn = api.dmAwardGold;
      if (ma.type === 'penalty-gold') fn = api.dmPenaltyGold;
      if (ma.type === 'damage') fn = api.dmDamage;
      if (ma.type === 'heal') fn = api.dmHeal;

      await fn(ma.userId, data);
      this.notify('success', 'Aplicado', 'Ação executada com sucesso');
      this.closeAction();
      await this.loadRoster();
    } catch (e) {
      var msg = (e.response && e.response.data && e.response.data.error) || 'Falha ao aplicar ação';
      this.notify('danger', 'Erro', msg);
    }
  }

  render() {
    var session = this.state.session;
    var roster = this.state.roster;

    return (
      <React.Fragment>
        <div className="rna-wrapper"><NotificationAlert ref="notificationAlert" /></div>
        <SimpleHeader name="🐉 Mestre do Jogo" way={[{ name: "RPG", url: "/dm" }]} />

        <Container className="mt--6" fluid>
          {/* Aviso: turma sem perguntas cadastradas */}
          {this.state.questionCount === 0 && (
            <Card className="shadow mb-3" style={{ background: '#fff3cd', border: '1px solid #ffeeba' }}>
              <CardBody className="py-3 d-flex justify-content-between align-items-center flex-wrap" style={{ color: '#856404', gap: '8px' }}>
                <span>
                  <i className="fas fa-exclamation-triangle mr-2" />
                  <strong>Sem perguntas os inimigos não têm desafio!</strong> Cadastre no Banco de Perguntas 📚
                </span>
                <Button color="warning" size="sm" onClick={() => this.setState({ questionBankOpen: true })}>
                  📚 Cadastrar agora
                </Button>
              </CardBody>
            </Card>
          )}

          {/* Controles de sessão */}
          <Card className="shadow mb-3">
            <CardBody>
              <Row className="align-items-center">
                <Col md="6">
                  <h3 className="mb-1">
                    <i className="fas fa-dragon mr-2 text-danger" />
                    Sessão de Aula
                  </h3>
                  {session ? (
                    <p className="mb-0">
                      <BadgeUI color="success" pill className="mr-2">AO VIVO</BadgeUI>
                      Aula {session.classNumber}
                      {session.bossEligible && <BadgeUI color="warning" pill className="ml-2">BOSS HOJE!</BadgeUI>}
                    </p>
                  ) : (
                    <p className="mb-0 text-muted">Nenhuma sessão ativa</p>
                  )}
                </Col>
                <Col md="6" className="text-right">
                  <Button color="primary" size="sm" onClick={() => this.setState({ questionBankOpen: true })} className="mr-2 mb-1">
                    📚 Banco de Perguntas
                    {this.state.questionCount !== null && (
                      <BadgeUI color="light" pill className="ml-1" style={{ color: '#333' }}>{this.state.questionCount}</BadgeUI>
                    )}
                  </Button>
                  {!session ? (
                    <Button color="success" size="lg" onClick={() => this.startSession()} className="mb-1">
                      <i className="fas fa-play mr-2" />
                      Iniciar Sessão
                    </Button>
                  ) : (
                    <React.Fragment>
                      <Button color="warning" size="sm" onClick={() => this.spawnEnemy('common')} className="mr-2 mb-1">
                        <i className="fas fa-spider mr-1" /> Spawnar Inimigo
                      </Button>
                      <Button color="danger" size="sm" onClick={() => this.spawnEnemy('boss')} className="mr-2 mb-1">
                        <i className="fas fa-dragon mr-1" /> BOSS
                      </Button>
                      <Button color="info" size="sm" onClick={() => this.spawnEvent()} className="mr-2 mb-1">
                        Evento Aleatório 🎲
                      </Button>
                      <Button color="secondary" size="sm" onClick={() => this.endSession()} className="mb-1">
                        <i className="fas fa-stop" /> Encerrar
                      </Button>
                    </React.Fragment>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Lista de alunos */}
          <Row>
            {this.state.loading ? (
              <Col className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-muted" /></Col>
            ) : roster.length === 0 ? (
              <Col className="text-center py-5 text-muted">
                <i className="fas fa-users fa-3x mb-2" style={{ opacity: 0.3 }} />
                <p>Nenhum aluno com personagem RPG ainda. Eles entram automaticamente ao acessar /character-rpg.</p>
              </Col>
            ) : (
              roster.map((s) => (
                <Col md="6" lg="4" key={s.user} className="mb-3">
                  <Card className="shadow-sm" style={{
                    border: s.currentHp <= 0 ? '2px solid #dc3545' : '1px solid #eee',
                    opacity: s.currentHp <= 0 ? 0.7 : 1
                  }}>
                    <CardHeader className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        <i className={'fas ' + (s.currentHp <= 0 ? 'fa-skull text-danger' : 'fa-user-shield text-primary') + ' mr-2'} />
                        {s.name} {s.lastName || ''}
                      </h6>
                      {s.level && (
                        <BadgeUI color="warning" pill style={{ fontSize: '0.65rem' }}>
                          Nv {s.level}
                        </BadgeUI>
                      )}
                    </CardHeader>
                    <CardBody style={{ padding: '12px 16px' }}>
                      <HpBar currentHp={s.currentHp} maxHp={s.maxHp} compact />
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <small className="text-muted">{s.xp || 0} XP</small>
                        <GoldDisplay balance={s.gold} compact />
                      </div>

                      {/* Actions */}
                      <div className="mt-3 d-flex flex-wrap" style={{ gap: '4px' }}>
                        <Button size="sm" color="success" outline title="Dar XP" onClick={() => this.openAction(s.user, 'award-xp')}>
                          <i className="fas fa-bolt" /> +XP
                        </Button>
                        <Button size="sm" color="warning" outline title="Dar Gold" onClick={() => this.openAction(s.user, 'award-gold')}>
                          <i className="fas fa-coins" /> +🪙
                        </Button>
                        <Button size="sm" color="info" outline title="Curar" onClick={() => this.openAction(s.user, 'heal')}>
                          <i className="fas fa-heart" /> +HP
                        </Button>
                        <Button size="sm" color="danger" outline title="Tirar Gold" onClick={() => this.openAction(s.user, 'penalty-gold')}>
                          <i className="fas fa-coins" /> -🪙
                        </Button>
                        <Button size="sm" color="dark" outline title="Causar Dano" onClick={() => this.openAction(s.user, 'damage')}>
                          <i className="fas fa-fist-raised" /> -HP
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        </Container>

        {/* Banco de Perguntas */}
        <QuestionBankModal
          isOpen={this.state.questionBankOpen}
          toggle={() => this.setState({ questionBankOpen: !this.state.questionBankOpen })}
          classroom={this.state.classroomId}
          notify={(type, title, message) => this.notify(type, title, message)}
          onCountChange={(count) => this.setState({ questionCount: count })}
        />

        {/* Modal de ação */}
        {this.state.modalAction && (
          <Modal isOpen toggle={() => this.closeAction()}>
            <ModalHeader toggle={() => this.closeAction()}>
              {this.state.modalAction.type === 'award-xp' && '⚡ Conceder XP'}
              {this.state.modalAction.type === 'award-gold' && '🪙 Conceder Gold'}
              {this.state.modalAction.type === 'penalty-gold' && '🪙 Tirar Gold'}
              {this.state.modalAction.type === 'damage' && '💥 Causar Dano'}
              {this.state.modalAction.type === 'heal' && '❤️ Curar'}
            </ModalHeader>
            <ModalBody>
              <div className="mb-3">
                <label>Quantidade:</label>
                <Input
                  type="number"
                  value={this.state.actionAmount}
                  onChange={(e) => this.setState({ actionAmount: e.target.value })}
                  min="1"
                  max={this.getActionLimit(this.state.modalAction.type)}
                />
                <small className="text-muted">
                  Limite: {this.getActionLimit(this.state.modalAction.type)} {this.getActionUnit(this.state.modalAction.type)} por ação
                </small>
              </div>
              <div className="mb-3">
                <label>Motivo (opcional):</label>
                <Input
                  type="text"
                  placeholder="ex: Resposta correta na aula"
                  value={this.state.actionReason}
                  onChange={(e) => this.setState({ actionReason: e.target.value })}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => this.closeAction()}>Cancelar</Button>
              <Button color="primary" onClick={() => this.confirmAction()}>Confirmar</Button>
            </ModalFooter>
          </Modal>
        )}
      </React.Fragment>
    );
  }
}

export default connect(state => ({
  user: state.user,
  permission: state.permission
}))(TeacherDungeonMaster);
